import { Add, Delete, Edit, Home, Refresh, RemoveRedEye } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { DataTable, KebabMenu, PageToolbar, SiteSelect, StyledCard } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { APITokenModel } from "types";
import { dateFormat } from "utils";
import AddAPIToken from "./add";
import EditAPIToken from "./edit";
import RemoveAPIToken from "./remove";
import TokenShow from "./token";
import ViewAPIToken from "./view";
interface Data {
  apiTokens?: APITokenModel[];
  totalRecords?: number;
}

export default function Users() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<APITokenModel>();
  const [viewing, setViewing] = useState<APITokenModel>();
  const [removing, setRemoving] = useState<APITokenModel>();
  const [{ userData }] = useContextController();
  const [siteId, setSiteId] = useState<string>();
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<keyof APITokenModel>();
  const [isAscending, setAscending] = useState(false);

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const { apiTokens, totalRecords } = useMemo(() => {
    const data = response && response.data;
    // console.log(data);
    if (data) {
      return data;
    }
    return {};
  }, [response]);
  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onSort = useCallback(
    (val: keyof APITokenModel) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!siteId) return;
    let params = `?limit=${limit}&page=${page + 1}&siteId=${siteId}`;
    if (search) {
      params += `&search=${search}`;
    }
    if (sort) {
      if (isAscending) {
        params += `&sort=${sort}`;
      } else {
        params += `&sort=-${sort}`;
      }
    }
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.APIToken + params).catch(console.error);
    }, 300);
    return () => {
      if (searchWaitRef.current) clearTimeout(searchWaitRef.current);
    };
  }, [page, limit, search, sort, isAscending, reload, siteId, callFetchGet]);

  useEffect(() => setPage(0), [search]);

  useTitle("API Tokens");

  return (
    <PrivateLayout canAccess={(type, role) => !(type === "user" && role === "user")}>
      {adding && <AddAPIToken onSuccess={onReload} onClose={() => setAdding(false)} />}
      {editing && <EditAPIToken token={editing} onSuccess={onReload} onClose={() => setEditing(undefined)} />}
      {viewing && <ViewAPIToken token={viewing} onClose={() => setViewing(undefined)} />}
      {removing && <RemoveAPIToken token={removing} onSuccess={onReload} onClose={() => setRemoving(undefined)} />}
      <PageToolbar
        title={t["API Token Records"]}
        search={search}
        searchPlaceholder={t["e.g Auth Token"]}
        onSearchChange={setSearch}
        breadcrumbs={
          <Breadcrumbs aria-label="breadcrumb">
            <Tooltip title={t["Home"]}>
              <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
                <Home fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography color="text.primary">{t["API Tokens"]}</Typography>
          </Breadcrumbs>
        }
        actions={
          <>
            <Box maxWidth={220} minWidth={180}>
              <SiteSelect
                adminOnly
                value={siteId}
                onChange={(nextSiteId) => {
                  setSiteId(nextSiteId);
                  if (nextSiteId) {
                    localStorage.setItem("siteId", nextSiteId);
                  }
                }}
                hideLabel
              />
            </Box>
            {userData?.type !== "user" || userData?.role !== "user" ? (
              <Tooltip title={t["Add API Token"]}>
                <IconButton
                  onClick={() => setAdding(true)}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            ) : null}
            {isLoading ? (
              <IconButton disabled>
                <CircularProgress size={20} sx={{ color: COLORS.primary }} />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />

      <Box width="100%">
        <StyledCard>
          <CardContent sx={{ pt: 2 }}>
            <DataTable
              rows={apiTokens || null}
              isLoading={isLoading}
              error={error}
              sort={sort}
              isAscending={isAscending}
              onSort={onSort}
              columns={[
                {
                  header: t["Name"],
                  accessor: "name",
                  sort: "name"
                },
                {
                  header: t["Expiry Date"],
                  accessor: (row) => (row.expiry ? dateFormat(row.expiry) : "Never"),
                  sort: "expiry"
                },
                {
                  header: t["Token"],
                  accessor: (row) => (
                    <Typography>
                      <TokenShow token={row.token} />
                    </Typography>
                  )
                },
                {
                  header: t["Site"],
                  accessor: (row) => row.site?.name
                },
                {
                  header: t["Added On"],
                  sort: "createdAt",
                  accessor: (row) => dateFormat(row.createdAt || "")
                },
                {
                  header: t["Actions"],
                  align: "center",
                  accessor: (row) => (
                    <KebabMenu
                      items={[
                        {
                          key: "view",
                          label: t["View"],
                          icon: <RemoveRedEye fontSize="small" />,
                          onClick: () => setViewing(row)
                        },
                        {
                          key: "edit",
                          label: t["Edit"],
                          icon: <Edit fontSize="small" />,
                          onClick: () => setEditing(row)
                        },
                        {
                          key: "delete",
                          label: t["Delete"],
                          icon: <Delete fontSize="small" />,
                          destructive: true,
                          onClick: () => setRemoving(row)
                        }
                      ]}
                    />
                  )
                }
              ]}
            />
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end", borderBottom: "8px" }}>
            <StyledTablePagination
              component="div"
              align="right"
              count={totalRecords || 0}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </CardActions>
        </StyledCard>
      </Box>
    </PrivateLayout>
  );
}
